// NK-351. What delivered completes are actually worth.
//
// Once a rate has changed mid-field, completes x the project-level CPI stops being
// the answer. Each delivered complete carries the rate it landed at, and the project
// rate only describes the newest stretch, so multiplying by it prices the whole run
// at the latest number. Summing the rates really run at is the only total that
// matches what gets paid - or, on the client side, invoiced.
//
// Named for the vendor side because that is where the workflow started, but nothing
// in here is vendor-specific: client CPI derives its rates from the completes the
// same way, so the client revenue totals use these too.

import { ProjectSurveyPartner } from '../models/project';
import { VendorCpiSpan, VendorCpiTier } from '../models/vendor-cpi-change';

export function vendorCpiTiersCost(tiers: VendorCpiTier[]): number {
  if (tiers == null || tiers.length === 0) {
    return 0;
  }

  return tiers.reduce((total, tier) => total + (tier.completes * tier.cpi), 0);
}

// NK-351. The tier total, but only when the tiers describe the same completes the
// caller is pricing. Null means they do not, so the caller keeps its own figure.
//
// Client tiers cover every complete in a segment, and the cards that price them
// deliberately leave some out - the synthetic panel's test traffic, most of all,
// which is real ResolvedParticipant rows the client is not billed for. Summing the
// tiers there would quietly bill for it. When the two populations agree the tier sum
// is exactly right, and when they do not, completes x the current rate is the closer
// of the two wrong answers.
export function cpiTiersCostForCompletes(tiers: VendorCpiTier[], completes: number): number | null {
  if (tiers == null || tiers.length === 0) {
    return null;
  }

  const covered = tiers.reduce((total, tier) => total + tier.completes, 0);
  return covered === completes ? vendorCpiTiersCost(tiers) : null;
}

// NK-351. The allocation a vendor is working to, which is what bounds the stretch a
// new rate covers. Soft Launch vendors are held to the soft quota; everyone else to
// the full one. Shared so the badge and the price change modal bound the forward
// stretch identically - if they disagreed, the tooltip and the modal that wrote it
// would describe the same change differently.
export function vendorCpiAllocation(partner: ProjectSurveyPartner): number {
  if (partner == null) {
    return 0;
  }

  return partner.partnerStatus === 'Soft Launch' ? partner.partnerSoftQuota : partner.partnerFullQuota;
}

// NK-351. A vendor's rates as one timeline: the stretches the delivered completes
// carry, then the rate the next complete will land at.
//
// The delivered stretches remain the only billable truth and nothing here changes
// that - every money figure still derives from the completes alone, through the
// functions above. The timeline exists because the badge sits beside the vendor's
// *current* CPI, and a forward change puts a rate on that value which no complete
// carries yet. Counting delivered rates alone then reads as a contradiction on
// screen: "$0.75" next to "2 RATES", with $0.75 in neither of them. The modal that
// applied the change already previews the pending stretch, so including it here is
// what makes the two agree rather than a new idea.
//
// The delivered tiers are sorted rather than trusted. The backend returns them in
// delivery order today, but a timeline read out of order is worse than no timeline,
// and the pending stretch has to be able to find the last delivered complete.
export function vendorCpiTimeline(tiers: VendorCpiTier[], currentCpi?: number, limit?: number): VendorCpiSpan[] {
  if (tiers == null || tiers.length === 0) {
    return [];
  }

  const delivered: VendorCpiSpan[] = [...tiers]
    .sort((a, b) => a.firstComplete - b.firstComplete)
    .map(tier => ({ ...tier, isPending: false, isOpenEnded: false }));

  const lastDelivered = delivered[delivered.length - 1];
  const allocation = limit ?? 0;

  // No pending stretch when the rate has not moved, or when there is no rate to show.
  // $0 is a real vendor rate, so only a missing one counts as no rate.
  if (currentCpi == null || currentCpi === lastDelivered.cpi) {
    return delivered;
  }

  // A vendor at or over its allocation still gets the pending stretch: overs happen, and
  // the rate beside the badge is the one they will land at. There is just no limit left
  // to end the stretch at, so it is open-ended rather than an inverted range.
  const firstComplete = lastDelivered.lastComplete + 1;
  const isOpenEnded = allocation < firstComplete;

  return [
    ...delivered,
    {
      cpi: currentCpi,
      completes: 0,
      firstComplete,
      lastComplete: isOpenEnded ? firstComplete : allocation,
      isPending: true,
      isOpenEnded
    }
  ];
}

// NK-351. How many rates the vendor is priced at, delivered and pending.
//
// Not the same as `ProjectSurveyPartner.CpiSplitCount`, which is a costing trigger:
// it reads 2 after a forward change because the project rate has stopped describing
// the delivered rows, and it stays 2 however many changes follow. The timeline counts
// the rates that actually exist.
export function vendorCpiRateCount(tiers: VendorCpiTier[], currentCpi?: number, limit?: number): number {
  return vendorCpiTimeline(tiers, currentCpi, limit).length;
}

// The rates behind the badge, so it can say what they are rather than only how many.
// One line per rate with the stretch of completes it covers, oldest first. The pending
// stretch is marked in words rather than styled: ngx-bootstrap's tooltip renders a
// plain string and ignores `data-html`, so markup shows through as text. Newline
// separated for the same reason - the hosts pass `containerClass="nav-rates-tooltip"`,
// which sets `pre-line`.
export function vendorCpiRatesTooltip(tiers: VendorCpiTier[], currentCpi?: number, limit?: number): string {
  return vendorCpiTimeline(tiers, currentCpi, limit)
    .map(span => {
      const stretch = span.isOpenEnded
        ? `Completes ${span.firstComplete.toLocaleString()}+`
        : span.firstComplete === span.lastComplete
          ? `Complete ${span.firstComplete.toLocaleString()}`
          : `Completes ${span.firstComplete.toLocaleString()} to ${span.lastComplete.toLocaleString()}`;
      const suffix = span.isPending ? ' (current)' : '';
      return `$${(span.cpi || 0).toFixed(2)} - ${stretch}${suffix}`;
    })
    .join('\n');
}
